import { useRef, type ReactNode } from 'react';
import { formatMoney, formatDate, addMonthsIso, todayIso, clampIso } from '../lib/format';
import type { Currency } from '../lib/types';

interface Props {
  viewDate: string;
  onViewDateChange: (iso: string) => void;
  bookValue: number;
  runnerCount: number;
  /** Placement-count credit sum (0.5/0.25 per role) for the same runners. */
  runnersBySplit: number;
  startingSoonCount: number;
  currency: Currency;
  /** When set, a GBP/USD display-currency toggle is shown. */
  onCurrencyChange?: (c: Currency) => void;
  /** Today's book, shown under the projection when viewing a later date. */
  todayBook?: { value: number; runners: number };
  /** Extra controls for the header (e.g. the team filter). */
  toolbar?: ReactNode;
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="12" height="11" rx="1" />
      <path d="M2 6 L14 6" />
      <path d="M5 1.5 L5 4.5" />
      <path d="M11 1.5 L11 4.5" />
    </svg>
  );
}

/** Page header + headline tiles, in the Client Dashboard's style. */
export function BookValueHeader({
  viewDate,
  onViewDateChange,
  bookValue,
  runnerCount,
  runnersBySplit,
  startingSoonCount,
  currency,
  onCurrencyChange,
  todayBook,
  toolbar,
}: Props) {
  const today = todayIso();
  const maxDate = addMonthsIso(today, 12);
  const inputRef = useRef<HTMLInputElement>(null);
  const isToday = viewDate === today;

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // fall through to focus + click fallback
      }
    }
    input.focus();
    input.click();
  };

  const runnersBySplitLabel = runnersBySplit.toLocaleString('en-GB', { maximumFractionDigits: 2 });

  // Quick jumps across the 12-month window.
  const jumps: Array<{ label: string; iso: string }> = [
    { label: 'Today', iso: today },
    { label: '+1 month', iso: addMonthsIso(today, 1) },
    { label: '+3 months', iso: addMonthsIso(today, 3) },
    { label: '+6 months', iso: addMonthsIso(today, 6) },
    { label: '+12 months', iso: maxDate },
  ];

  return (
    <>
      <div className="ds-ph">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          WGP forecast <em>on</em>
          <span style={{ position: 'relative' }}>
            <button type="button" className="ds-date-btn" onClick={openPicker} title="Pick the forecast date">
              <CalendarIcon />
              {formatDate(viewDate)}
              <span aria-hidden="true" style={{ fontSize: 12, opacity: 0.8 }}>▾</span>
            </button>
            <input
              ref={inputRef}
              type="date"
              value={viewDate}
              min={today}
              max={maxDate}
              onChange={(e) => onViewDateChange(clampIso(e.target.value, today, maxDate))}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
            />
          </span>
        </h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {onCurrencyChange && (
            <div className="seg-toggle" role="group" aria-label="Currency">
              {(['GBP', 'USD'] as const).map((c) => (
                <button key={c} type="button" className={currency === c ? 'active' : ''} onClick={() => onCurrencyChange(c)}>
                  {c === 'GBP' ? 'UK £' : 'USA $'}
                </button>
              ))}
            </div>
          )}
          {toolbar}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '-6px 0 18px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Jump to</span>
        <div className="seg-toggle" role="group" aria-label="Forecast date">
          {jumps.map((j) => (
            <button key={j.label} type="button" className={viewDate === j.iso ? 'active' : ''} onClick={() => onViewDateChange(j.iso)}>
              {j.label}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>or click the date to pick any day in the next 12 months</span>
      </div>

      <div className="ds-heads" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div className="ds-tile" style={{ ['--cat' as string]: 'var(--cont)' }}>
          <span className="ds-lab">Projected book</span>
          <span className="ds-val">
            {formatMoney(bookValue, currency)}
            <small>/wk</small>
          </span>
          <span className="ds-cmp">
            {isToday || !todayBook
              ? `weekly gross profit ${isToday ? 'today' : `on ${formatDate(viewDate)}`}`
              : `${formatMoney(todayBook.value, currency)}/wk today`}
          </span>
        </div>
        <div className="ds-tile" style={{ ['--cat' as string]: 'var(--cont)' }}>
          <span className="ds-lab">Runners</span>
          <span className="ds-val">{runnerCount}</span>
          <span className="ds-cmp">
            {isToday || !todayBook ? 'contractors on site' : `${todayBook.runners} today`}
          </span>
        </div>
        <div className="ds-tile" style={{ ['--cat' as string]: 'var(--cont)' }}>
          <span className="ds-lab">Runners by split</span>
          <span className="ds-val">{runnersBySplitLabel}</span>
          <span className="ds-cmp">Consultant + AO 0.5 each · 0.25 on 4-way deals</span>
        </div>
        <div className="ds-tile" style={{ ['--cat' as string]: 'var(--perm)' }}>
          <span className="ds-lab">Starting soon</span>
          <span className="ds-val">{startingSoonCount}</span>
          <span className="ds-cmp">new starters in the 12 months from {isToday ? 'today' : formatDate(viewDate)}</span>
        </div>
      </div>
    </>
  );
}
